<template>
  <button
    class="puzzle-navigation"
    :class="{ 'puzzle-navigation--won' : gameWon }"
    :data-navposition="position"
    @mouseenter="hoverIn"
    @mouseleave="hoverOut"
  >
    <svg viewBox="0 0 80 1">
      <line x1="0" y1="1" x2="80" y2="1" />
    </svg>
    <transition @enter="enter" @leave="leave">
      <span :key="content">
        <p>
          {{ content }}
        </p>
      </span>
    </transition>
  </button>
</template>

<script>
import anime from 'animejs';
import { SplitText } from '@/assets/libs/SplitText';
import { animations } from '@/scripts/animations';

export default {
  name: 'PuzzleNavigation',

  props: {
    position: {
      type: String,
      required: true,
    },
    gameWon: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    content() {
      let content;

      if (this.position === 'left') {
        content = !this.gameWon ? this.$translate('PREV') : this.$translate('YOU');
      } else {
        content = !this.gameWon ? this.$translate('NEXT') : this.$translate('WIN');
      }

      return content;
    },
  },

  methods: {
    enter(element, done) {
      this.$el.removeAttribute('data-animated');
      const split = new SplitText(element, { type: 'chars', charsClass: 'paragraph__char' });
      if (this.gameWon) {
        this.$el.removeAttribute('data-animated');
        const line = this.$el.querySelector('svg line');
        const transformOrigin = this.position === 'left' ? '100% 50% 0' : '0% 50% 0';
        anime({
          targets: line,
          scaleX: 1,
          duration: 300,
          transformOrigin: [transformOrigin, transformOrigin],
          easing: 'easeInOutSine',
        }, 0);
        anime({
          targets: split.chars,
          translateX: [(this.position === 'left' ? -1 : 1) * 50, 0],
          opacity: [0, 1],
          duration: 1800,
          delay: anime.stagger(80, {
            start: 200,
            from: this.position === 'left' ? 'last' : 'first',
          }),
          complete: () => {
            done();
          },
        });
      } else {
        anime({
          targets: split.chars,
          translateY: [50, 0],
          opacity: [0, 1],
          duration: 1800,
          delay: anime.stagger(80, {
            from: this.position === 'left' ? 'last' : 'first',
          }),
          complete: () => {
            this.$el.setAttribute('data-animated', '');
            done();
          },
        });
      }
    },
    leave(element, done) {
      if (this.gameWon) {
        done();
      } else {
        this.$el.removeAttribute('data-animated');
        const split = [...this.$el.querySelectorAll('.paragraph__char')];
        anime.set(element,
          {
            position: 'absolute',
            top: 0,
            [this.position === 'left' ? 'right' : 'left']: 170,
          });
        anime({
          targets: split,
          translateY: [0, -50],
          opacity: [1, 0],
          duration: 1800,
          delay: anime.stagger(80, {
            from: this.position === 'left' ? 'last' : 'first',
          }),
          complete: () => {
            done();
          },
        });
      }
    },
    hoverIn() {
      if (!this.$el.hasAttribute('data-animated')) return;
      animations.get('navigationItemHoverIn')(this.$el);
    },
    hoverOut() {
      if (!this.$el.hasAttribute('data-animated')) return;
      animations.get('navigationItemHoverOut')(this.$el);
    },
  },
};
</script>

<style lang="scss" scoped>
.puzzle-navigation {
  position: relative;
  flex: 1 1 300px;

  @include mq(dl) {
    flex: 1 1 400px;
  }

  display: flex;
  align-items: center;
  justify-content: flex-start;

  color: $color-beige;
  font-weight: 500;
  transform: translateY(35px);

  z-index: 0;

  @include fluid-type(
    1024px,
    2560px,
    40px,
    80px,
    40px,
    80px
  );

  @include mq($until: ipadL) {
    display: none;
  }

  svg {
    width: 80px;
    margin: 0 45px 0 45px;

    fill: none;
    stroke: $color-beige;
  }

  &:first-of-type {
    flex-direction: row-reverse;
  }
}
</style>
<style>
.puzzle-navigation--won {
  pointer-events: none;
}
</style>
